import type { ReactNode } from "react";
import { SITE_URL } from "@/lib/seo";
import { breadcrumbJsonLd, faqPageJsonLd, type FaqEntry } from "@/lib/structuredData";
import {
  APP_TO_SAVE_ORGANIZE_SOCIAL_POSTS,
  BLOG_POST,
  COMPARE,
  ORGANIZE_SAVED_REELS,
  RECIME_ALTERNATIVE,
  SAVE_EVENTS_FROM_INSTAGRAM_TIKTOK,
  SAVE_GIFT_IDEAS_FROM_SOCIAL_MEDIA,
  SAVE_PLACES_FROM_TIKTOK,
  SAVE_RECIPES_FROM_INSTAGRAM_TIKTOK,
  SAVE_TIKTOKS,
  SAVE_TRAVEL_IDEAS_FROM_INSTAGRAM_TIKTOK,
  STACKLIST_ALTERNATIVE,
} from "@/routes";

/**
 * Copy for the SEO use-case landing pages.
 *
 * Provenance: assembled from the approved library (docs/content/AGENT.md,
 * docs/content/published/boilerplate.md, app store full description) with
 * net-new lines tracked in docs/content/drafts/website/. Don't rewrite lines
 * here without checking that doc — much of this is approved copy verbatim.
 */

export interface UseCaseConfig {
  path: string;
  breadcrumbName: string;
  metaTitle: string;
  metaDescription: string;
  ogImage?: string;
  /** Adds the desktop-only Get started link beside the hero and closing store badges. */
  showWebStart?: boolean;
  heroTitle: ReactNode;
  heroSubhead: string;
  problem: { heading: string; paragraphs: string[] };
  solutions: Array<{ heading: string; body: string }>;
  featureSpotlight?: {
    heading: string;
    body: string;
    imageSrc: string;
    imageAlt: string;
    caption: string;
    /** External setup links, shown at desktop widths only. */
    links?: Array<{ label: string; href: string }>;
  };
  examples?: {
    heading: string;
    body: string;
    cards: Array<{
      id: string;
      title: string;
      description: string;
      sourceLabel?: string;
      platform?: string;
      tags: string[];
    }>;
  };
  steps: Array<{ title: string; body: string }>;
  faq: FaqEntry[];
  related: Array<{ label: string; to: string }>;
  jsonLd: Array<Record<string, unknown>>;
}

const marketingOgImage = (filename: string): string => `${SITE_URL}/og/${filename}`;

const withFaqJsonLd = (config: Omit<UseCaseConfig, "jsonLd">): UseCaseConfig => ({
  ...config,
  jsonLd: [
    faqPageJsonLd(config.faq),
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: config.breadcrumbName, path: config.path },
    ]),
  ],
});

const savePlacesFromTiktok = withFaqJsonLd({
  path: SAVE_PLACES_FROM_TIKTOK(),
  showWebStart: true,
  breadcrumbName: "Save places from TikTok",
  metaTitle: "Save Places from TikTok & Instagram to Your Map | Stasht",
  metaDescription:
    "That restaurant you saved on TikTok lands on your map, organized automatically, and shows up when you're nearby. Free on iOS, Android, and desktop.",
  heroTitle: (
    <>
      Save places from TikTok. <span className="text-brand-500">See them on your map.</span>
    </>
  ),
  heroSubhead:
    "Restaurants, bars, coffee shops, hotels, hikes. Every place you stash lands on your personal map and shows up when you're nearby.",
  problem: {
    heading: "The save was the easy part",
    paragraphs: [
      "You see a place on TikTok or Instagram, you save it, and that's where it ends. The video is in one app, the name is in a screenshot, and the address is nowhere. When you're finally in the neighborhood, there's no way to bring it all back.",
      "It's not your fault. The tools just weren't built to do anything with what you saved.",
    ],
  },
  solutions: [
    {
      heading: "Your saves, on a map",
      body: "Every place you stash — restaurants, bars, coffee shops, hotels, hikes — lands on your personal map. Browse by neighborhood, filter by category, or just see what's around you right now.",
    },
    {
      heading: "Every detail, pulled out",
      body: "We extract the place name, address, hours, links, and prices from the post automatically. No folders. No sorting. No work on your part.",
    },
    {
      heading: "Right place, right moment",
      body: "That restaurant someone posted on Instagram shows up on your map when you're nearby. Planning a trip? Pull up the map for any city and see everything you've saved there.",
    },
    {
      heading: "Works with every app you save from",
      body: "Instagram, TikTok, X, Reddit, YouTube, Pinterest, and the web. One map for all of it, on iPhone, iPad, Android, and desktop.",
    },
  ],
  examples: {
    heading: "Real TikTok saves, mapped",
    body: "These are real public TikTok saves in Stasht. Some are restaurants, one is a scenic bench, and one is an activity roundup where the place signal has to come from the video context.",
    cards: [
      {
        id: "803b21b1-dbca-467d-8bd2-8ad1c73d0610",
        title: "Authentic Syrian Shawarma at Casa Habibi",
        description: "A restaurant video becomes a useful place save with the original TikTok still attached.",
        sourceLabel: "Real public TikTok save",
        platform: "TikTok",
        tags: ["restaurant", "shawarma", "map"],
      },
      {
        id: "c6345d9a-9f02-4d9c-97af-4870b129a9f0",
        title: "Dining Experience at Saltwood Ferrymead",
        description: "A TikTok recommendation works better when it becomes an actual mapped place.",
        sourceLabel: "Real public TikTok save",
        platform: "TikTok",
        tags: ["restaurant", "christchurch", "map"],
      },
      {
        id: "76b31a7f-db8e-48f7-a177-8795e2828b10",
        title: "Exploring Oeschinensee's Scenic Bench",
        description: "Even a random bench can be worth saving when the useful output is a map pin.",
        sourceLabel: "Real public TikTok save",
        platform: "TikTok",
        tags: ["travel", "switzerland", "map"],
      },
      {
        id: "5f3af3a3-18a9-4909-9f1e-e5cb5d2f3c2c",
        title: "Explore Unique Activities in Atlanta This Summer",
        description: "The caption does not always hand you the place cleanly. The video context still matters.",
        sourceLabel: "Real public TikTok save",
        platform: "TikTok",
        tags: ["atlanta", "activities", "map"],
      },
    ],
  },
  steps: [
    { title: "Stash it", body: "Share any TikTok or Reel to Stasht, right from the share sheet. Two taps." },
    {
      title: "We do the rest",
      body: "Stasht finds the place in the video, pulls out the details, and pins it to your map.",
    },
    {
      title: "It comes back",
      body: "Your saves show up when you're nearby, when you search, and when you're planning.",
    },
  ],
  faq: [
    {
      question: "How does Stasht get the place out of a TikTok?",
      answer:
        "Stasht reads what's in the post — the caption, the text on screen, even what's said in the video — and identifies the places mentioned. The restaurant gets a pin, an address, hours, and links, automatically.",
    },
    {
      question: "Does it work with Instagram Reels too?",
      answer:
        "Yes. Instagram, TikTok, X, Reddit, YouTube, Pinterest, and the web. If you can share it, you can stash it.",
    },
    {
      question: "What if a video covers five places?",
      answer:
        "Stasht can pick up several places from one video. Identified places can appear on your map with the details Stasht finds, but it won't always get a clean pin for each one, so glance at the map after you save.",
    },
    {
      question: "Can I get back to the original video?",
      answer: "Always. Every save links back to the original post, so the source is one tap away.",
    },
    {
      question: "Is Stasht free?",
      answer: "Yes. We're early and focused on building the best experience we can. Just download and start stashing.",
    },
  ],
  related: [
    { label: "How to save TikTok restaurants to a map", to: BLOG_POST({ slug: "how-to-save-tiktok-restaurants-to-a-map" }) },
    { label: "You saved the restaurant. Here's how to actually go.", to: BLOG_POST({ slug: "tiktok-restaurants-how-to-actually-go" }) },
    { label: "The best apps for saving places from TikTok, compared", to: BLOG_POST({ slug: "best-apps-to-save-places-from-tiktok" }) },
    { label: "How Stasht compares to other save apps", to: COMPARE() },
  ],
});

const organizeSavedReels = withFaqJsonLd({
  path: ORGANIZE_SAVED_REELS(),
  showWebStart: true,
  breadcrumbName: "Organize saved Reels",
  metaTitle: "Organize Your Saved Reels Automatically | Stasht",
  metaDescription:
    "Saved Reels, organized automatically. Stasht pulls the places, recipes, workouts, and ideas out of every video you save. Free on iOS, Android, and desktop.",
  ogImage: marketingOgImage("organize-saved-reels.png"),
  heroTitle: (
    <>
      Your saved Reels, <span className="text-brand-500">organized for you.</span>
    </>
  ),
  heroSubhead:
    "No folders. No sorting. No work on your part. Stasht understands what's in every Reel you save and organizes it the way you already think about it.",
  problem: {
    heading: "Instagram saves stay on Instagram",
    paragraphs: [
      "They don't tell you what's inside, they don't organize themselves, and they don't come back to you. A saved Reel is a thumbnail in a grid, and the recipe inside it might as well be invisible.",
      "Collections help until they don't. Nobody opens an app to file saves into folders, and the folder you need is never the one you made.",
    ],
  },
  solutions: [
    {
      heading: "Every detail, pulled out",
      body: "We extract places, events, links, and prices automatically. A recipe Reel becomes a recipe. A restaurant Reel becomes a place on your map. A workout Reel is ready for the gym.",
    },
    {
      heading: "Tags, notes, and search",
      body: "Find a Reel by the place, date, or word you remember, or describe something you saw in it. Notes and tags are optional. Add one when you want to remember why you stashed it.",
    },
    {
      heading: "Bring it all together",
      body: "Instagram saves, TikTok collections, X bookmarks, YouTube links, screenshots. Everything comes into one place, organized the way you already think about it.",
    },
    {
      heading: "Import what you already have",
      body: "Stasht's browser extensions import existing saves from Instagram, TikTok, Reddit, YouTube, Pinterest, and more. Chrome handles desktop import. Safari works on Mac, iPhone, and iPad: log into supported sites in Safari to bulk import, or stash pages and posts as you browse.",
    },
  ],
  featureSpotlight: {
    heading: "Bulk import with Chrome or Safari.",
    body: "Bulk import with Chrome on desktop or Safari on Mac, iPhone, and iPad. In Safari, log into the supported sites you want to import from, then bring those saves into Stasht.",
    imageSrc: "/videos/stasht-safari-extension-demo.gif",
    imageAlt: "Demo showing the Stasht Safari extension saving from the browser on iPhone",
    caption: "Saving from Safari into Stasht with the iOS Safari extension.",
    links: [
      {
        label: "Chrome extension",
        href: "https://chromewebstore.google.com/detail/stasht-app-saves-that-wor/pnmkndkigheholgjnjklmaahpfanhcdg",
      },
      {
        label: "Safari for Mac",
        href: "https://apps.apple.com/us/app/stasht-app-for-safari/id6780505483?mt=12",
      },
    ],
  },
  steps: [
    {
      title: "Stash it",
      body: "Share a Reel to Stasht from the share sheet, or bulk import with Chrome on desktop or Safari on Mac, iPhone, and iPad.",
    },
    {
      title: "We do the rest",
      body: "Stasht can pick up details from the post's text, what's said, and what's shown on screen, so it's easier to find later.",
    },
    {
      title: "Find it later",
      body: "Search by what you remember, browse by category, or let the right save find you at the right time.",
    },
  ],
  faq: [
    {
      question: "Can Instagram organize saved Reels into folders?",
      answer:
        "Instagram has collections, and they work if you sort every save manually and remember which folder you used. Stasht does the organizing for you, automatically, and works across every platform.",
    },
    {
      question: "Can I import the Reels I've already saved?",
      answer:
        "Yes. Stasht can bulk import existing saves with the Chrome extension on desktop or the Safari extension on Mac, iPhone, and iPad. In Safari, log into the supported sites you want to import from, then bring those saves into Stasht.",
    },
    {
      question: "What happens if a Reel gets taken down?",
      answer:
        "Stasht keeps the details we pulled out — the place, the recipe, your notes and tags — and always links back to the original post.",
    },
    {
      question: "Does this work for TikToks and YouTube too?",
      answer:
        "Yes. Instagram, X, TikTok, Reddit, YouTube, Pinterest, and the web. Your stash syncs across iPhone, iPad, Android, and desktop.",
    },
    {
      question: "Is Stasht free?",
      answer: "Yes. We're early and focused on building the best experience we can. Just download and start stashing.",
    },
  ],
  related: [
    { label: "How to organize saved Instagram Reels: 5 honest methods", to: BLOG_POST({ slug: "how-to-organize-saved-instagram-reels" }) },
    { label: "The best ways to save and organize social media posts", to: BLOG_POST({ slug: "best-ways-to-save-and-organize-social-media-posts" }) },
    { label: "How Stasht compares to other save apps", to: COMPARE() },
  ],
});

const saveTiktoks = withFaqJsonLd({
  path: SAVE_TIKTOKS(),
  showWebStart: true,
  breadcrumbName: "Save TikToks",
  metaTitle: "Save TikToks You Can Actually Find Later | Stasht",
  metaDescription:
    "A save-for-later app is easy. Stasht is a find-it-later app: TikToks organized automatically and brought back when they matter. Free on iOS, Android, and desktop.",
  ogImage: marketingOgImage("save-tiktoks.png"),
  heroTitle: (
    <>
      Don&apos;t just save TikToks. <span className="text-brand-500">Find them later.</span>
    </>
  ),
  heroSubhead:
    "Stash what you find and we'll do the rest. Restaurants land on your map, events on your calendar, recipes ready when you cook.",
  problem: {
    heading: "Saved, then what?",
    paragraphs: [
      "You save constantly. The place for your next date night, the drill for your serve, the gift for your sister. TikTok files them all under one bookmark icon, and that's where it ends. Stasht is where it begins.",
      "It's not your fault. The tools just weren't built to do anything with what you saved.",
    ],
  },
  solutions: [
    {
      heading: "Right thing, right moment",
      body: "Planning a night out? Check your map for places Stasht identified, see dates it picked up from posts, and set a reminder so the plan comes back when you want it.",
    },
    {
      heading: "Reminders for later",
      body: "Set a reminder on any stash, right when you save it. That recipe you want to try this weekend? We'll bring it back at the right time.",
    },
    {
      heading: "Tags, notes, and search",
      body: "Add a note to remember why you stashed something. Tag it however you want. Or don't — everything is searchable anyway.",
    },
    {
      heading: "Save from (almost) anywhere",
      body: "Instagram, TikTok, Facebook, X, YouTube, and more. Set up the share extension once and stash from any app in seconds. Your stashes sync across iPhone, iPad, Android, and desktop.",
    },
  ],
  steps: [
    { title: "Stash it", body: "Share any TikTok to Stasht. Set up the share extension once, then it's two taps." },
    {
      title: "We do the rest",
      body: "We figure out what's in the video — the place, the event, the product — and organize it automatically.",
    },
    {
      title: "It comes back",
      body: "On your map, on your calendar, in search, or as a reminder. Right thing, right moment.",
    },
  ],
  faq: [
    {
      question: "Does Stasht download TikTok videos?",
      answer:
        "No. Stasht saves the post, pulls out what matters — places, events, products, steps — and always links back to the original on TikTok. You get the useful part without a camera roll full of videos.",
    },
    {
      question: "How is this different from TikTok Favorites?",
      answer:
        "Favorites keeps your saves inside TikTok, in one long list. Stasht works across every platform, tells you what's inside each save, and brings them back on your map, calendar, and reminders.",
    },
    {
      question: "Can I search what's said in a video?",
      answer:
        "Yes. Stasht reads text on screen and listens to what's said, so you can search for that pasta with the brown butter and actually find it.",
    },
    {
      question: "What else can I save besides TikToks?",
      answer:
        "Instagram, X, Reddit, YouTube, Pinterest, and the web. Bulk import with Chrome on desktop or Safari on Mac, iPhone, and iPad.",
    },
    {
      question: "Is Stasht free?",
      answer: "Yes. We're early and focused on building the best experience we can. Just download and start stashing.",
    },
  ],
  related: [
    { label: "How to find your saved TikToks", to: BLOG_POST({ slug: "how-to-find-your-saved-tiktoks" }) },
    { label: "Save places from TikTok to your map", to: SAVE_PLACES_FROM_TIKTOK() },
    { label: "How Stasht compares to other save apps", to: COMPARE() },
  ],
});

const appToSaveAndOrganizeSocialPosts = withFaqJsonLd({
  path: APP_TO_SAVE_ORGANIZE_SOCIAL_POSTS(),
  showWebStart: true,
  breadcrumbName: "Save and organize social media posts",
  metaTitle: "App to Save and Organize Social Media Posts | Stasht",
  metaDescription:
    "Stasht saves posts from Instagram, TikTok, X, YouTube, Reddit, Pinterest, and the web, then organizes them into maps, calendars, reminders, and searchable collections.",
  ogImage: marketingOgImage("app-to-save-and-organize-social-media-posts.png"),
  heroTitle: (
    <>
      The app to save and organize <span className="text-brand-500">social media posts.</span>
    </>
  ),
  heroSubhead:
    "Stash posts, screenshots, and links from Instagram, TikTok, X, YouTube, Reddit, Pinterest, and the web. Stasht pulls out the details and brings them back on your map, calendar, reminders, and search.",
  problem: {
    heading: "Every app has a save button. None of them work together.",
    paragraphs: [
      "Your restaurants are in TikTok, recipes in Instagram, links in X, videos in YouTube, and screenshots in your camera roll. The problem is not saving. The problem is finding anything again.",
      "Stasht gives those saves one place to land, then turns them into useful, searchable cards instead of another forgotten pile.",
    ],
  },
  solutions: [
    {
      heading: "One stash across platforms",
      body: "Save from Instagram, TikTok, Facebook, X, Reddit, YouTube, Pinterest, Safari, Chrome, and the web. Your stash syncs across iPhone, iPad, Android, and desktop.",
    },
    {
      heading: "Automatic organization",
      body: "Stasht reads captions, text on screen, links, and speech in videos, then pulls out places, dates, products, recipes, workouts, and ideas.",
    },
    {
      heading: "Maps, calendars, reminders",
      body: "Saved restaurants land on your map. Events land on your calendar. Anything can get a reminder. The save comes back when it is useful.",
    },
    {
      heading: "Search what was inside",
      body: 'Find a save by the place, ingredient, event, product, creator, tag, note, or phrase you remember. Or search what you remember seeing, like "person holding a mic." No folder discipline required.',
    },
  ],
  steps: [
    { title: "Stash it", body: "Share a post, link, screenshot, or video to Stasht from the apps you already use." },
    {
      title: "We pull out the details",
      body: "Stasht turns the save into a useful card with extracted places, dates, text, categories, and source links.",
    },
    {
      title: "Find it again",
      body: "Browse maps, calendars, collections, reminders, and search when you are ready to actually use what you saved.",
    },
  ],
  faq: [
    {
      question: "What social media posts can I save in Stasht?",
      answer:
        "Instagram, TikTok, Facebook, X, Reddit, YouTube, Pinterest, screenshots, and normal web links. If you can share it, you can usually stash it.",
    },
    {
      question: "Does Stasht download social media videos?",
      answer:
        "No. Stasht saves the useful details and links back to the original source. It is built for finding and organizing saves, not filling your camera roll with downloaded videos.",
    },
    {
      question: "How does Stasht organize saves automatically?",
      answer:
        "Stasht reads available signals like captions, web metadata, text on screen, and speech in videos, then creates searchable cards with places, dates, categories, tags, and source links.",
    },
    {
      question: "Is Stasht free?",
      answer: "Yes. We're early and focused on building the best experience we can. Just download and start stashing.",
    },
  ],
  related: [
    { label: "The best ways to save and organize social media posts", to: BLOG_POST({ slug: "best-ways-to-save-and-organize-social-media-posts" }) },
    { label: "Save places from TikTok to your map", to: SAVE_PLACES_FROM_TIKTOK() },
    { label: "Organize saved Instagram Reels automatically", to: ORGANIZE_SAVED_REELS() },
  ],
});

const saveRecipesFromInstagramAndTiktok = withFaqJsonLd({
  path: SAVE_RECIPES_FROM_INSTAGRAM_TIKTOK(),
  showWebStart: true,
  breadcrumbName: "Save recipes from Instagram and TikTok",
  metaTitle: "Save Recipes from Instagram and TikTok | Stasht",
  metaDescription:
    "Save recipes from Instagram Reels, TikTok, YouTube, Pinterest, and the web. Stasht makes recipes searchable and keeps the original post one tap away.",
  heroTitle: (
    <>
      Save recipes from Instagram and TikTok. <span className="text-brand-500">Actually find them later.</span>
    </>
  ),
  heroSubhead:
    "Stash recipe videos, screenshots, and links. Stasht pulls out what it can, keeps the source, and makes everything searchable when it is time to cook.",
  problem: {
    heading: "Recipe saves disappear exactly when dinner starts",
    paragraphs: [
      "The pasta looked perfect in a Reel. The chicken marinade was in a TikTok. The grocery list is now a memory test across five apps.",
      "Stasht keeps those recipes with the rest of your life: notes, tags, source links, reminders, and search in one place.",
    ],
  },
  solutions: [
    {
      heading: "Save recipe videos and links",
      body: "Share Instagram Reels, TikToks, YouTube videos, Pinterest pins, screenshots, and web recipes into one stash.",
    },
    {
      heading: "Search by what you remember",
      body: "Find the brown butter pasta, raspberry bites, weeknight chicken, or creator name without scrolling through a saved-video grid.",
    },
    {
      heading: "Keep the source attached",
      body: "Every recipe links back to the original post, so you can return to the exact video, creator, comments, or website.",
    },
    {
      heading: "Not just recipes",
      body: "Recipes sit beside restaurants, workouts, products, events, and travel ideas, so Stasht becomes one place for everything you save.",
    },
  ],
  steps: [
    { title: "Stash the recipe", body: "Share the Reel, TikTok, screenshot, or web page to Stasht." },
    { title: "Add context", body: "Use notes, tags, and reminders so the recipe is ready when you are." },
    { title: "Cook from search", body: "Search the dish, ingredient, creator, or phrase you remember and open the original source." },
  ],
  faq: [
    {
      question: "Can Stasht save recipes from Instagram Reels and TikTok?",
      answer:
        "Yes. You can share recipe videos and links to Stasht from Instagram, TikTok, YouTube, Pinterest, and the web.",
    },
    {
      question: "Is Stasht a dedicated meal planner?",
      answer:
        "No. Stasht is broader than a recipe-only meal planner. It is for saving and finding recipes alongside places, products, events, workouts, articles, and other social saves.",
    },
    {
      question: "Can I save recipes I already saved on Instagram?",
      answer:
        "Yes. Stasht can import existing Instagram saves with Chrome on desktop or Safari on Mac, iPhone, and iPad. New recipes can be shared directly into Stasht from mobile.",
    },
    {
      question: "Is Stasht free?",
      answer: "Yes. We're early and focused on building the best experience we can. Just download and start stashing.",
    },
  ],
  related: [
    { label: "How to save recipes from Instagram and TikTok", to: BLOG_POST({ slug: "how-to-save-recipes-from-instagram-and-tiktok" }) },
    { label: "ReciMe alternative for more than recipes", to: RECIME_ALTERNATIVE() },
    { label: "How to organize saved Instagram Reels", to: BLOG_POST({ slug: "how-to-organize-saved-instagram-reels" }) },
  ],
});

const saveTravelIdeasFromInstagramAndTiktok = withFaqJsonLd({
  path: SAVE_TRAVEL_IDEAS_FROM_INSTAGRAM_TIKTOK(),
  showWebStart: true,
  breadcrumbName: "Save travel ideas from Instagram and TikTok",
  metaTitle: "Save Travel Ideas from Instagram and TikTok | Stasht",
  metaDescription:
    "Save travel ideas, hotels, restaurants, hikes, and guides from Instagram, TikTok, YouTube, and the web. Stasht organizes them into a searchable trip stash.",
  heroTitle: (
    <>
      Save travel ideas from social. <span className="text-brand-500">Turn them into a trip stash.</span>
    </>
  ),
  heroSubhead:
    "Hotels, restaurants, hikes, city guides, museums, beaches, and neighborhood tips stay findable across Instagram, TikTok, YouTube, Pinterest, and the web.",
  problem: {
    heading: "Travel inspiration is easy to collect and hard to use",
    paragraphs: [
      "A month before the trip, every video feels useful. During the trip, the exact cafe, hike, or neighborhood guide is buried in an app you are not opening.",
      "Stasht keeps travel saves searchable, tied to the source, and ready to browse by place when planning actually starts.",
    ],
  },
  solutions: [
    {
      heading: "Save every trip idea",
      body: "Stash places, hotels, restaurants, hikes, guides, packing tips, and itineraries from the apps where you find them.",
    },
    {
      heading: "Browse places on a map",
      body: "When a save includes a real place, Stasht can put it where it belongs: on your map instead of inside a social feed.",
    },
    {
      heading: "Plan by city or vibe",
      body: "Use search, tags, notes, and categories to pull together the saves that matter for a destination, weekend, or kind of trip.",
    },
    {
      heading: "Keep the original context",
      body: "Every save links back to the original video or article, so you can reopen the full tip before you book or go.",
    },
  ],
  steps: [
    { title: "Stash travel finds", body: "Share the post, video, screenshot, or article to Stasht as soon as you see it." },
    { title: "Let details collect", body: "Stasht keeps source links, extracted places, notes, and categories together." },
    { title: "Plan from one place", body: "Search the destination, open your map, and build the trip from the saves you already made." },
  ],
  faq: [
    {
      question: "Can I save travel ideas from TikTok and Instagram?",
      answer:
        "Yes. Stasht works with TikTok, Instagram, YouTube, Pinterest, screenshots, and normal web links, so travel ideas can live together.",
    },
    {
      question: "Does every travel save show up on a map?",
      answer:
        "Only saves with a place signal belong on the map. Stasht keeps other travel ideas searchable as cards with notes, tags, and source links.",
    },
    {
      question: "Can I use Stasht before I know my exact itinerary?",
      answer:
        "Yes. Save loosely first, then search by city, place, category, or note when you are ready to turn inspiration into a plan.",
    },
    {
      question: "Is Stasht free?",
      answer: "Yes. We're early and focused on building the best experience we can. Just download and start stashing.",
    },
  ],
  related: [
    { label: "How to turn social media travel saves into a trip plan", to: BLOG_POST({ slug: "turn-social-media-travel-saves-into-a-trip-plan" }) },
    { label: "Save places from TikTok and Instagram to your map", to: SAVE_PLACES_FROM_TIKTOK() },
    { label: "The best apps for saving places from TikTok", to: BLOG_POST({ slug: "best-apps-to-save-places-from-tiktok" }) },
  ],
});

const saveEventsFromInstagramAndTiktok = withFaqJsonLd({
  path: SAVE_EVENTS_FROM_INSTAGRAM_TIKTOK(),
  showWebStart: true,
  breadcrumbName: "Save events from Instagram and TikTok",
  metaTitle: "Save Events from Instagram and TikTok to Your Calendar | Stasht",
  metaDescription:
    "Save concerts, pop-ups, shows, drops, and local events from Instagram, TikTok, Facebook, and the web. Stasht helps bring dates back on your calendar and reminders.",
  heroTitle: (
    <>
      Save events from social. <span className="text-brand-500">Bring dates back when they matter.</span>
    </>
  ),
  heroSubhead:
    "Concerts, pop-ups, markets, shows, launches, and ticket drops are easy to miss when they stay buried in feeds. Stasht keeps event saves searchable and ready for calendars and reminders.",
  problem: {
    heading: "The event you saved is usually gone by the time you remember it",
    paragraphs: [
      "Social feeds are good at creating urgency and terrible at preserving it. You save the post, then forget the date, venue, ticket link, or sale window.",
      "Stasht treats event saves like things with time attached, not just another thumbnail in a grid.",
    ],
  },
  solutions: [
    {
      heading: "Save concerts, pop-ups, and launches",
      body: "Share event posts from Instagram, TikTok, Facebook, X, YouTube, newsletters, and the web into one place.",
    },
    {
      heading: "Dates become useful",
      body: "When Stasht can identify a date, the event can live with your calendar flow instead of only in the source app.",
    },
    {
      heading: "Set reminders immediately",
      body: "Add a reminder when you save something, so ticket sales, pop-ups, and one-night events do not depend on memory.",
    },
    {
      heading: "Keep venue and source context",
      body: "Save the venue, creator, source link, notes, and tags together so you can come back with the full context.",
    },
  ],
  steps: [
    { title: "Stash the post", body: "Share the event post, listing, or screenshot to Stasht." },
    { title: "Capture the signals", body: "Stasht keeps the source and pulls out useful signals like places, dates, and categories when available." },
    { title: "Come back in time", body: "Use calendar views, reminders, and search before the event disappears into last week." },
  ],
  faq: [
    {
      question: "Can Stasht save events from Instagram and TikTok?",
      answer:
        "Yes. Save event posts, videos, and links from Instagram, TikTok, Facebook, X, YouTube, and the web.",
    },
    {
      question: "Does Stasht add events to my calendar automatically?",
      answer:
        "Stasht is built around calendar-aware saves. When event details are available, Stasht can help keep dates useful through calendar views and reminders.",
    },
    {
      question: "What kinds of events should I stash?",
      answer:
        "Concerts, pop-ups, markets, restaurant events, local shows, product drops, ticket sales, workshops, and anything else with a date you do not want to lose.",
    },
    {
      question: "Is Stasht free?",
      answer: "Yes. We're early and focused on building the best experience we can. Just download and start stashing.",
    },
  ],
  related: [
    { label: "How to stop missing events saved on social", to: BLOG_POST({ slug: "how-to-stop-missing-events-saved-on-instagram" }) },
    { label: "Save TikToks so they are findable later", to: SAVE_TIKTOKS() },
    { label: "Save places from TikTok to your map", to: SAVE_PLACES_FROM_TIKTOK() },
  ],
});

const saveGiftIdeasFromSocialMedia = withFaqJsonLd({
  path: SAVE_GIFT_IDEAS_FROM_SOCIAL_MEDIA(),
  showWebStart: true,
  breadcrumbName: "Save gift ideas from social media",
  metaTitle: "Save Gift Ideas from Instagram, TikTok, and the Web | Stasht",
  metaDescription:
    "Save gift ideas from Instagram, TikTok, YouTube, Pinterest, shopping sites, and the web. Stasht keeps ideas searchable with notes, tags, links, and reminders.",
  heroTitle: (
    <>
      Save gift ideas from social. <span className="text-brand-500">Find them before the birthday.</span>
    </>
  ),
  heroSubhead:
    "Stash products, creator picks, wish-list hints, shopping links, and screenshots. Add notes and reminders so the perfect idea is not buried when you need it.",
  problem: {
    heading: "Gift ideas arrive months before the occasion",
    paragraphs: [
      "You see the right thing while scrolling, save it somewhere, and then try to remember where when the birthday, holiday, or anniversary is suddenly close.",
      "Stasht gives gift ideas a useful home: source links, notes, reminders, tags, and search.",
    ],
  },
  solutions: [
    {
      heading: "Save products and ideas",
      body: "Stash TikToks, Reels, YouTube videos, Pinterest pins, shopping links, screenshots, and web pages.",
    },
    {
      heading: "Remember who it was for",
      body: "Add notes and tags for people, occasions, price ranges, or themes so gift ideas stay connected to the reason you saved them.",
    },
    {
      heading: "Remind yourself later",
      body: "Set reminders on promising ideas before birthdays, holidays, trips, or deadlines sneak up.",
    },
    {
      heading: "Keep the original link",
      body: "Open the source post or store page when you are ready to buy, compare, or send it to someone else.",
    },
  ],
  steps: [
    { title: "Stash the idea", body: "Share a product post, link, or screenshot to Stasht." },
    { title: "Add a note", body: "Tag the person, occasion, or price range while the reason is fresh." },
    { title: "Buy when it matters", body: "Use reminders and search when the occasion gets close." },
  ],
  faq: [
    {
      question: "Can I save gift ideas from Instagram and TikTok?",
      answer:
        "Yes. Stasht works with Instagram, TikTok, YouTube, Pinterest, screenshots, shopping pages, and normal web links.",
    },
    {
      question: "Can I organize gifts by person?",
      answer:
        "Yes. Use tags and notes for people, occasions, price ranges, and ideas. You can also search when you only remember part of the idea.",
    },
    {
      question: "Can Stasht remind me to buy a gift?",
      answer:
        "Yes. You can set reminders on saves so a gift idea comes back before the birthday, holiday, or deadline.",
    },
    {
      question: "Is Stasht free?",
      answer: "Yes. We're early and focused on building the best experience we can. Just download and start stashing.",
    },
  ],
  related: [
    { label: "App to save and organize social media posts", to: APP_TO_SAVE_ORGANIZE_SOCIAL_POSTS() },
    { label: "Organize saved Instagram Reels automatically", to: ORGANIZE_SAVED_REELS() },
    { label: "The best ways to save and organize social media posts", to: BLOG_POST({ slug: "best-ways-to-save-and-organize-social-media-posts" }) },
  ],
});

const recimeAlternative = withFaqJsonLd({
  path: RECIME_ALTERNATIVE(),
  breadcrumbName: "ReciMe alternative",
  metaTitle: "ReciMe Alternative for More Than Recipes | Stasht",
  metaDescription:
    "Looking for a ReciMe alternative? Stasht saves recipes from Instagram and TikTok, plus restaurants, events, travel ideas, products, workouts, and links.",
  ogImage: marketingOgImage("recime-alternative.png"),
  heroTitle: (
    <>
      A ReciMe alternative for saves <span className="text-brand-500">beyond recipes.</span>
    </>
  ),
  heroSubhead:
    "ReciMe is a strong recipe-first app. Stasht is for people whose social saves include recipes, restaurants, travel, events, workouts, products, articles, and ideas in one place.",
  problem: {
    heading: "Recipe apps solve one important slice",
    paragraphs: [
      "If your main problem is meal planning, grocery lists, and recipe management, a dedicated recipe app can be the right tool.",
      "If your saves are mixed across food, places, products, travel, events, articles, and videos, you need a broader stash.",
    ],
  },
  solutions: [
    {
      heading: "Recipes are included",
      body: "Save recipe Reels, TikToks, YouTube videos, Pinterest pins, screenshots, and web recipes, with source links kept attached.",
    },
    {
      heading: "Places go to maps",
      body: "Restaurant and travel saves can become place cards on your map, so a food video can turn into somewhere you actually visit.",
    },
    {
      heading: "Events and reminders matter too",
      body: "Stasht is built for event dates, reminders, and search, not only cooking workflows.",
    },
    {
      heading: "One app for all save behavior",
      body: "Keep recipes next to workouts, products, articles, places, and trip ideas, because that is how social feeds actually work.",
    },
  ],
  steps: [
    { title: "Use ReciMe if", body: "You mainly want a dedicated recipe organizer, grocery lists, and meal planning." },
    { title: "Use Stasht if", body: "You save recipes plus many other kinds of posts and want one searchable place for all of them." },
    { title: "Keep sources close", body: "Stasht links back to every original post so the creator context stays one tap away." },
  ],
  faq: [
    {
      question: "Is Stasht better than ReciMe?",
      answer:
        "It depends on the job. ReciMe is purpose-built for recipes and meal planning. Stasht is broader: recipes, places, events, products, travel ideas, workouts, articles, and more.",
    },
    {
      question: "Can Stasht save recipes from Instagram and TikTok?",
      answer:
        "Yes. Stasht can save recipe posts and videos from Instagram, TikTok, YouTube, Pinterest, screenshots, and the web.",
    },
    {
      question: "Does Stasht have grocery lists or meal planning?",
      answer:
        "No. If grocery lists and meal planning are the core workflow, ReciMe may be a better fit. Stasht is focused on saving, organizing, searching, and resurfacing all kinds of social and web saves.",
    },
    {
      question: "Is Stasht free?",
      answer: "Yes. We're early and focused on building the best experience we can. Just download and start stashing.",
    },
  ],
  related: [
    { label: "Save recipes from Instagram and TikTok", to: SAVE_RECIPES_FROM_INSTAGRAM_TIKTOK() },
    { label: "App to save and organize social media posts", to: APP_TO_SAVE_ORGANIZE_SOCIAL_POSTS() },
    { label: "How Stasht compares to other save apps", to: COMPARE() },
  ],
});

const stacklistAlternative = withFaqJsonLd({
  path: STACKLIST_ALTERNATIVE(),
  breadcrumbName: "Stacklist alternative",
  metaTitle: "Stacklist Alternative for Auto-Organized Social Saves | Stasht",
  metaDescription:
    "Looking for a Stacklist alternative? Stasht saves social posts and web links, then auto-organizes places, events, recipes, products, reminders, and searchable cards.",
  heroTitle: (
    <>
      A Stacklist alternative for saves that <span className="text-brand-500">organize themselves.</span>
    </>
  ),
  heroSubhead:
    "Stacklist is built around saving and sharing things into cards and stacks. Stasht is for social saves that should turn into maps, calendars, reminders, and searchable collections automatically.",
  problem: {
    heading: "Manual collections are useful until the pile gets big",
    paragraphs: [
      "Cards and stacks are a familiar way to save links and share collections. They work best when you know exactly where each thing belongs.",
      "Stasht focuses on the next step: understanding what was inside the save and bringing it back by context.",
    ],
  },
  solutions: [
    {
      heading: "Save social posts and links",
      body: "Stash Instagram, TikTok, Facebook, X, Reddit, YouTube, Pinterest, screenshots, and normal web pages.",
    },
    {
      heading: "Extract useful signals",
      body: "Stasht reads captions, text on screen, speech in videos, and metadata to pull out places, dates, categories, and searchable details.",
    },
    {
      heading: "Resurface by context",
      body: "Saved places can show up on your map, events in calendar flows, and anything can come back through reminders and search.",
    },
    {
      heading: "Less filing, more finding",
      body: "Use notes and tags when you want control, but do not depend on perfect manual organization to find the save later.",
    },
  ],
  steps: [
    { title: "Use Stacklist if", body: "You mainly want card-based collections and sharing around links and things you find online." },
    { title: "Use Stasht if", body: "You want social saves interpreted into maps, calendars, reminders, and searchable cards." },
    { title: "Save first", body: "Stasht lets you capture quickly, then organize through extracted details, tags, notes, and search." },
  ],
  faq: [
    {
      question: "Is Stasht a Stacklist alternative?",
      answer:
        "Yes, if you are looking for one place to save social posts and web links. Stasht differs by focusing on automatic extraction, maps, calendars, reminders, and search.",
    },
    {
      question: "When should I choose Stacklist?",
      answer:
        "Choose Stacklist if your main need is card-based collections and sharing. Choose Stasht if the value is having saves understood, organized, and resurfaced by context.",
    },
    {
      question: "Can Stasht save normal web links too?",
      answer:
        "Yes. Stasht works with web links as well as social posts, screenshots, and videos from the apps where you already save things.",
    },
    {
      question: "Is Stasht free?",
      answer: "Yes. We're early and focused on building the best experience we can. Just download and start stashing.",
    },
  ],
  related: [
    { label: "App to save and organize social media posts", to: APP_TO_SAVE_ORGANIZE_SOCIAL_POSTS() },
    { label: "Save TikToks so they are findable later", to: SAVE_TIKTOKS() },
    { label: "How Stasht compares to other save apps", to: COMPARE() },
  ],
});

export const USE_CASE_CONFIGS: Record<string, UseCaseConfig> = {
  [appToSaveAndOrganizeSocialPosts.path]: appToSaveAndOrganizeSocialPosts,
  [savePlacesFromTiktok.path]: savePlacesFromTiktok,
  [organizeSavedReels.path]: organizeSavedReels,
  [saveTiktoks.path]: saveTiktoks,
  [saveRecipesFromInstagramAndTiktok.path]: saveRecipesFromInstagramAndTiktok,
  [saveTravelIdeasFromInstagramAndTiktok.path]: saveTravelIdeasFromInstagramAndTiktok,
  [saveEventsFromInstagramAndTiktok.path]: saveEventsFromInstagramAndTiktok,
  [saveGiftIdeasFromSocialMedia.path]: saveGiftIdeasFromSocialMedia,
  [recimeAlternative.path]: recimeAlternative,
  [stacklistAlternative.path]: stacklistAlternative,
};
